import { useQuery, useMutation } from '@tanstack/react-query'
import { getTickets, getTicketById, createTicket, updateTicket, uploadEvidence, markTicketRead, getUnreadMessages } from '@/api/ticket'
import { toast } from '@/hooks/use-toast'
import { queryClient } from '@/main'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'
import { useUserStore } from '@/stores/userStore'

export const useTicketsQuery = (params: {
  page: number
  limit: number
  status?: "open" | "in_progress" | "blocked" | "resolved"
  type?: TicketType
  search?: string
}) => {
  const queryParams = {
    page: params.page,
    limit: params.limit,
    ...(params.status && { status: params.status }),
    ...(params.type && { type: params.type }),
    ...(params.search && { search: params.search }),
  } as GetTicketsParams
  
  return useQuery({
    queryKey: ['tickets', queryParams],
    queryFn: () => getTickets(queryParams),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: true,
  })
}

export const useTicketByIdQuery = (ticketId: string) => {
  return useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => getTicketById({ ticket_id: ticketId }),
    enabled: !!ticketId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export const useCreateTicketMutation = () => {
  const { t } = useTranslation()
  
  return useMutation({
    mutationFn: createTicket,
    onSuccess: (data: CreateTicketResponse) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      toast({
        title: t(tokens.tickets.create.success_title),
        description: t(tokens.tickets.create.success_description),
      })
      return data
    },
    onError: () => {
      toast({
        title: t(tokens.tickets.create.error_title),
        description: t(tokens.tickets.create.error_description),
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateTicketMutation = () => {
  return useMutation({
    mutationFn: ({ data, externalId }: { data: UpdateTicketDTO; externalId: string }) => 
      updateTicket(data, externalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['ticket'] })
    },
  })
}

export const useUploadEvidenceMutation = () => {
  const { activeTeam } = useUserStore((state) => state.user)

  return useMutation({
    mutationFn: async ({ file, ticketId }: { file: File; ticketId: string }) => {
      if (!activeTeam?.external_id) {
        throw new Error('Organization ID not found')
      }

      const uploadResponse = await uploadEvidence({
        file,
        organization_id: activeTeam.external_id,
        ticket_id: ticketId
      })

      if (!uploadResponse.success || (!uploadResponse.evidence_url && !uploadResponse.file_path)) {
        throw new Error('Upload failed')
      }

      const fileExtension = file.name.split('.').pop() || ''
      const attachment: TicketAttachment = {
        external_id: `evidence_${Date.now()}`,
        name: file.name,
        extension: fileExtension,
        evidence_url: uploadResponse.evidence_url || uploadResponse.file_path || '',
        uploaded_at: new Date().toISOString(),
        type: "user"
      }

      return { attachment, uploadResponse }
    }
  })
}

export const useMarkTicketReadMutation = () => {
  return useMutation({
    mutationFn: ({ ticketId }: { ticketId: string }) => 
      markTicketRead({ ticket_id: ticketId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket'] })
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['unread-messages'] })
    },
  })
}

export const useUnreadMessagesQuery = () => {
  return useQuery({
    queryKey: ['unread-messages'],
    queryFn: getUnreadMessages,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
    refetchOnWindowFocus: true,
  })
}
